import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Dialog, DialogContent, ErrorState, Icon, Skeleton, Switch, cn, toast } from "@sagot/ui";
import { useState, type ReactNode } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { isPlanGateError, UpgradeCard } from "../shared/plan-gate";
import type { IdentityProviderOut } from "../shared/types";
import { CREATABLE_PROVIDERS, isCreatable, PROVIDER_ICON, type CreatableProvider } from "./audience-meta";
import { ProviderDetails } from "./provider-details";
import { ProviderDialog } from "./provider-dialog";
import { useCanManageAudiences, useDeleteProvider, useIdentityProviders, useToggleProvider } from "./use-audiences-data";

type Editing = { type: CreatableProvider; provider: IdentityProviderOut | null } | null;

function CardShell({ type, title, badge, description, children }: { type: IdentityProviderOut["type"]; title: string; badge: ReactNode; description: string; children: ReactNode }) {
  const icon = PROVIDER_ICON[type];
  return (
    <article className="card card-body">
      <div className="flex items-start gap-3">
        <span className={cn("inline-flex size-10 shrink-0 items-center justify-center rounded-lg", icon.box)}>
          <Icon name={icon.icon} className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-bold">{title}</h2>
            {badge}
          </div>
          <p className="mt-0.5 text-xs text-fg-muted">{description}</p>
        </div>
      </div>
      {children}
    </article>
  );
}

interface ProviderCardProps {
  provider: IdentityProviderOut;
  canManage: boolean;
  onConfigure: () => void;
  onDelete: () => void;
}

function ProviderCard({ provider, canManage, onConfigure, onDelete }: ProviderCardProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const toggle = useToggleProvider();
  const badge = provider.enabled ? (
    <Badge tone="success" dot>{t("deployKnowledge.audiences.idp.connected")}</Badge>
  ) : (
    <Badge>{t("deployKnowledge.audiences.idp.off")}</Badge>
  );
  const onToggle = (enabled: boolean) => {
    toggle.mutate({ id: provider.id, enabled }, {
      onSuccess: () => toast.success(enabled ? t("deployKnowledge.audiences.idp.enabledToast", { name: provider.name }) : t("deployKnowledge.audiences.idp.disabledToast", { name: provider.name })),
      onError: (caught) => toast.error(message(caught)),
    });
  };
  return (
    <CardShell type={provider.type} title={provider.name} badge={badge} description={t(`deployKnowledge.audiences.idp.descriptions.${provider.type}`)}>
      <ProviderDetails provider={provider} />
      <div className="mt-4 flex items-center justify-between gap-3 rounded-md border border-line px-3 py-2.5">
        <span className="text-sm font-semibold">{t("deployKnowledge.audiences.idp.enable")}</span>
        <Switch checked={provider.enabled} disabled={!canManage || toggle.isPending} aria-label={t("deployKnowledge.audiences.idp.enableNamed", { name: provider.name })} onCheckedChange={onToggle} />
      </div>
      {canManage && isCreatable(provider.type) ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={onConfigure}>
            <Icon name="settings" />
            {t("deployKnowledge.audiences.idp.configure")}
          </Button>
          <Button variant="ghost" size="sm" className="text-danger" onClick={onDelete}>
            <Icon name="unlock" />
            {t("deployKnowledge.audiences.idp.disconnect")}
          </Button>
        </div>
      ) : null}
      {!isCreatable(provider.type) ? <p className="mt-3 text-xs text-fg-subtle">{t("deployKnowledge.audiences.idp.rosterReadOnly")}</p> : null}
    </CardShell>
  );
}

function AvailableCard({ type, canManage, onConnect }: { type: CreatableProvider; canManage: boolean; onConnect: () => void }) {
  const { t } = useTranslation();
  return (
    <CardShell type={type} title={t(`deployKnowledge.audiences.idp.types.${type}`)} badge={<Badge>{t("deployKnowledge.audiences.idp.notConnected")}</Badge>} description={t(`deployKnowledge.audiences.idp.descriptions.${type}`)}>
      {canManage ? (
        <div className="mt-4 flex gap-2">
          <Button size="sm" onClick={onConnect}>
            <Icon name="plug" />
            {t("deployKnowledge.audiences.idp.connect")}
          </Button>
        </div>
      ) : null}
    </CardShell>
  );
}

function DeleteDialog({ provider, onClose }: { provider: IdentityProviderOut; onClose: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const remove = useDeleteProvider();
  const confirm = () => {
    remove.mutate(provider.id, {
      onSuccess: () => {
        toast.success(t("deployKnowledge.audiences.idp.deleted", { name: provider.name }));
        onClose();
      },
      onError: (caught) => toast.error(message(caught)),
    });
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t("deployKnowledge.audiences.idp.deleteTitle", { name: provider.name })}
        description={t("deployKnowledge.audiences.idp.deleteBody")}
        closeLabel={t("common.close")}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
            <Button variant="danger" onClick={confirm} loading={remove.isPending}>{t("deployKnowledge.audiences.idp.disconnect")}</Button>
          </>
        }
      />
    </Dialog>
  );
}

/** Identity providers tab: configured providers + the ones still available to connect. */
export function IdentityProvidersPanel() {
  const { t } = useTranslation();
  const providers = useIdentityProviders();
  const canManage = useCanManageAudiences();
  const [editing, setEditing] = useState<Editing>(null);
  const [deleting, setDeleting] = useState<IdentityProviderOut | null>(null);
  if (providers.isPending) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-40" />)}
      </div>
    );
  }
  if (providers.isError) {
    if (isPlanGateError(providers.error)) return <UpgradeCard feature={t("deployKnowledge.audiences.tabIdp")} />;
    return (
      <div className="card">
        <ErrorState title={t("deployKnowledge.loadError")} description={t("deployKnowledge.loadErrorBody")} onRetry={() => void providers.refetch()} retryLabel={t("common.retry")} />
      </div>
    );
  }
  const configured = new Set(providers.data.map((p) => p.type));
  const available = CREATABLE_PROVIDERS.filter((type) => !configured.has(type));
  return (
    <section className="grid grid-cols-1 gap-4 md:grid-cols-2" aria-label={t("deployKnowledge.audiences.tabIdp")}>
      {providers.data.map((provider) => (
        <ProviderCard
          key={provider.id}
          provider={provider}
          canManage={canManage}
          onConfigure={() => { if (isCreatable(provider.type)) setEditing({ type: provider.type, provider }); }}
          onDelete={() => { setDeleting(provider); }}
        />
      ))}
      {available.map((type) => (
        <AvailableCard key={type} type={type} canManage={canManage} onConnect={() => { setEditing({ type, provider: null }); }} />
      ))}
      {editing ? <ProviderDialog type={editing.type} provider={editing.provider} onClose={() => { setEditing(null); }} /> : null}
      {deleting ? <DeleteDialog provider={deleting} onClose={() => { setDeleting(null); }} /> : null}
    </section>
  );
}
